import React from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import LanguagePickerMenuItem from "./LanguagePickerMenuItem";
import { AccountFast } from "../core/_models_fast";
import AccountSelect from "./AccountSelect";
import useAccount from "../hooks/useAccount";
import useAccountChannel from "../hooks/useAccountChannel";
import { setCurrentAccountUniqueIdentifierInLocalStorage, useAxios } from "../core/_axios";
import useLocalizedCopy from "../hooks/useLocalizedCopy";
import { usePageHeader } from "../contexts/PageHeaderContext";
import { getGravatarUrl, getInitialsFromEmail } from "../utils/gravatar";

// When we deply on June 15th at 11PM EST, this banner will be empty.

const CURRENT_MESSAGE = `
`.trim() || null; // Set this to a message to show in the banner

/* ---------- small, reusable banner component ---------- */
const Banner: React.FC<{ variant: "error" | "warning" | "info"; children: React.ReactNode }> = ({
  variant,
  children,
}) => {
  let palette = "";
  switch (variant) {
    case "error":
      palette = "bg-red-50 border-red-300 text-red-800";
      break;
    case "warning":
      palette = "bg-yellow-50 border-yellow-300 text-yellow-800";
      break;
    case "info":
      palette = "bg-blue-50 border-blue-300 text-blue-800";
      break;
  }

  return (
    <div className={`flex flex-col mr-2`}>
      <div className={`px-4 py-2 rounded-md text-xs font-medium border ${palette}`}>
        {children}
      </div>
    </div>
  );
};

function Header() {
  const axios = useAxios();
  const { copy } = useLocalizedCopy();
  const { title: pageTitle, navigationItems } = usePageHeader();

  const {
    logout,
    currentUser: user,
    currentUserAccountUniqueIds,
    viewingAllAccounts,
    stopViewingIndividualAccount
  } = useAuth();

  const {
    account: currentAccount,
    setAccount: setCurrentAccount,
  } = useAccount({
    axios,
    isForCurrentAccount: true,
  });

  // Subscribe to account updates via WebSocket channel
  // Pass the actual account unique identifier (not the membership ID) to subscribe to the correct channel
  useAccountChannel({
    accountUniqueId: currentAccount?.unique_identifier,
    onAccountUpdated: (updatedAccount) => {
      // Update the current account when we receive an update
      if (currentAccount && updatedAccount.unique_identifier === currentAccount.unique_identifier) {
        // Merge the update with existing account data to preserve fast_billing and other fields
        setCurrentAccount({
          ...currentAccount,
          ...updatedAccount,
          // Preserve fast_billing if it's not in the update
          fast_billing: updatedAccount.fast_billing || currentAccount.fast_billing
        });
      }
    }
  });

  const [continuing, setContinuing] = React.useState(false);

  const [selectingAccount, setSelectingAccount] = React.useState(false);
  const [selectedAccount, setSelectedAccount] = React.useState<AccountFast | null>(null);

  const handleSelectedAccount = React.useCallback(async () => {
    if (!selectedAccount || continuing) {
      return;
    }
    setContinuing(true);
    setCurrentAccountUniqueIdentifierInLocalStorage(selectedAccount.unique_identifier);
    window.location.reload();
  }, [continuing, selectedAccount?.unique_identifier]);

  /* ---------- figure out which single banner to show ---------- */
  let banner: React.ReactNode = null;

  if (!viewingAllAccounts && currentAccount && currentAccount.fast_billing) {
    const { fast_billing } = currentAccount;

    if (fast_billing.enable_free_access) {
      // If free access is enabled, no banner is shown
    } else {

      const isSubscribed = fast_billing.is_subscribed;
      const cancelAt = fast_billing.stripe_subscription_cancel_at;
      const trialManuallyDisabled = fast_billing?.manually_disabled_free_trial;

      if (!isSubscribed && trialManuallyDisabled) {
        banner = (
          <Banner variant="error">
            {copy.Account.billing.title}.&nbsp;
            <Link className="underline" to="/manage-account/billing">
              {copy.Account.billing.changePlan}
            </Link>{" "}
            {copy.Common.labels.required}.
          </Banner>
        );
      } else if (cancelAt) {
        banner = (
          <Banner variant="warning">
            {copy.Account.billing.cancelSubscription} {new Date(cancelAt * 1000).toLocaleDateString()}.&nbsp;
            <Link className="underline" to="/manage-account/billing">
              {copy.Account.billing.changePlan}
            </Link>
            .
          </Banner>
        );
      } else if (!isSubscribed) {
        // Check if user is new (created within last 24 hours)
        const userCreatedAt = user.inserted_at ? new Date(user.inserted_at) : null;
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000);
        const isNewUser = userCreatedAt && userCreatedAt > oneDayAgo;

        // Only show trial banner after 24 hours
        if (!isNewUser) {
          banner = (
            <Banner variant="warning">
              {copy.Account.billing.banner.freeTrialExpiring.replace('{days}', '7')}&nbsp;
              <Link className="underline" to="/manage-account/billing">
                {copy.Account.billing.banner.upgradeNow}
              </Link>.
            </Banner>
          );
        }
      }
    }
  }

  // Show CURRENT_MESSAGE if set and no other banner is set
  if (CURRENT_MESSAGE && !banner && !user.is_super_super_user) {
    banner = (
      <Banner variant="info">
        {CURRENT_MESSAGE}
      </Banner>
    );
  }

  /* ---------- render ---------- */
  return (
    <header
      className="header fixed top-0 z-10 left-0 right-0 flex items-stretch shrink-0 bg-[#fefefe] dark:bg-coal-500"
      id="header"
      data-sticky
      data-sticky-class="shadow-sm dark:border-b dark:border-b-coal-100"
      data-sticky-name="header"
    >
      <div className="container-fixed flex justify-between items-center lg:gap-4 py-2">
        {/* logo / mobile nav button */}
        <div className="flex gap-1 lg:hidden items-center -ml-1">
          <Link className="shrink-0" to="/">
            <img className="max-h-[25px] w-full" src="/design/web_logo_black_small.png" />
          </Link>
          <div className="ms-3 flex items-center">
            <button className="btn btn-light btn-sm" data-drawer-toggle="#sidebar">
              {copy.Common.navigation.dashboard}
            </button>
          </div>
        </div>

        {/* Page title and navigation area */}
        <div className="flex items-center flex-grow" id="megamenu_container">
          {pageTitle && (
            <div className="flex items-center gap-4">
              <h1 className="text-xl font-semibold text-gray-900">{pageTitle}</h1>
              {navigationItems && (
                <div className="flex items-center gap-2">
                  {navigationItems}
                </div>
              )}
            </div>
          )}
        </div>

        {/* right-hand controls - more compact */}
        <div className="flex items-center gap-2 lg:gap-2.5">

          {selectingAccount ? <>
            <div className="flex items-center gap-2">
              <AccountSelect
                forceWidth
                onChange={(selectedOption) => {
                  setSelectedAccount(selectedOption);
                }}
              />
              <button
                className="btn btn-light btn-xs"
                type="button"
                onClick={() => setSelectingAccount(false)}
              >
                {copy.Common.buttons.cancel}
              </button>
              <button
                className="btn btn-primary btn-xs"
                type="button"
                onClick={handleSelectedAccount}
                disabled={continuing || !selectedAccount || selectedAccount.unique_identifier === currentAccount?.unique_identifier}
              >
                {copy.Common.buttons.update}
              </button>
            </div>

          </> : <>

            {banner}

            {/* Current Account name with subtle "Switch" underline */}
            {currentAccount && (
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700 font-medium">
                  {currentAccount.name}
                </span>
                {(user.is_super_super_user || (user.belongs_to_agency && user.is_agency_admin) || (currentUserAccountUniqueIds || []).length > 1) && <>
                  <button
                    type="button"
                    className="text-sm text-gray-500 underline underline-offset-2 hover:text-primary transition"
                    style={{ textDecorationThickness: '1px' }}
                    onClick={() => setSelectingAccount(true)}
                  >
                    {copy.Common.buttons.edit}
                  </button>
                </>}
              </div>
            )}

            {/* super-user "view all accounts" toggle */}
            {(user.is_super_super_user || (user.belongs_to_agency && user.is_agency_admin) || ((currentUserAccountUniqueIds || []).length > 1) || viewingAllAccounts) && (
              <label className="flex items-center text-sm gap-1.5 border-l pl-3">
                <span className="text-gray-600">{copy.Management.viewAllAccounts}</span>
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={viewingAllAccounts}
                  onChange={(e) => {
                    const isUnchecked = !e.target.checked;
                    // If unchecked, we show the account picker. If checked, we reload
                    if (isUnchecked) {
                      setSelectingAccount(true)
                    } else {
                      stopViewingIndividualAccount()
                    }
                  }}
                />
              </label>
            )}

          </>}


          {/* profile & language menus */}
          <div className="menu" data-menu="true">
            <div
              className="menu-item"
              data-menu-item-offset="20px, 10px"
              data-menu-item-placement="bottom-end"
              data-menu-item-toggle="dropdown"
              data-menu-item-trigger="click|lg:click"
            >
              <div className="menu-toggle btn btn-icon rounded-full border-2 border-success p-0 overflow-hidden">
                {user.email ? (
                  <img 
                    src={getGravatarUrl(user.email, 35)} 
                    alt={user.name || user.email}
                    className="w-[35px] h-[35px] rounded-full"
                    onError={(e) => {
                      // Fallback to initials if Gravatar fails
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const span = document.createElement('span');
                      span.className = 'symbol symbol-35';
                      span.textContent = user.name_initials || getInitialsFromEmail(user.email);
                      target.parentNode?.appendChild(span);
                    }}
                  />
                ) : (
                  <span className="symbol symbol-35">{user.name_initials || "?"}</span>
                )}
              </div>
              <div className="menu-dropdown menu-default light:border-gray-300 w-full max-w-[250px]">
                <div className="flex items-center justify-between px-5 py-1.5 gap-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="menu-toggle btn btn-icon rounded-full border-2 border-success p-0 overflow-hidden flex-shrink-0">
                      {user.email ? (
                        <img 
                          src={getGravatarUrl(user.email, 35)} 
                          alt={user.name || user.email}
                          className="w-[35px] h-[35px] rounded-full"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                            const span = document.createElement('span');
                            span.className = 'symbol symbol-35';
                            span.textContent = user.name_initials || getInitialsFromEmail(user.email);
                            target.parentNode?.appendChild(span);
                          }}
                        />
                      ) : (
                        <span className="symbol symbol-35">{user.name_initials || "?"}</span>
                      )}
                    </div>
                    <div className="flex flex-col gap-1.5 min-w-0">
                      {user.name && (
                        <span className="text-sm text-gray-800 font-semibold leading-none truncate">
                          {user.name}
                        </span>
                      )}
                      <span className="text-xs text-gray-600 font-medium leading-none truncate" title={user.email}>
                        {user.email}
                      </span>
                    </div>
                  </div>
                </div>

                <LanguagePickerMenuItem />

                <div className="menu-separator" />

                <div className="flex flex-col">
                  <div className="menu-item px-4 py-1.5">
                    <button
                      className="btn btn-sm btn-light justify-center"
                      data-dismiss="true"
                      onClick={logout}
                    >
                      {copy.Common.navigation.logout}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );

}

export default Header